import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, getDocs, orderBy, deleteDoc, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { Button, Spinner } from '../components/UI';
import GrowthChart from '../components/GrowthChart';
import ShareChildModal from '../components/ShareChildModal';
import ImmunizationScreen from './ImmunizationScreen';
import ActionSheet from '../components/ActionSheet';
import InterventionPlanCard from '../components/InterventionPlanCard'; 
import { calculateAge } from '../helpers/utils';

const TabButton = ({ label, isActive, onClick, icon }) => (
    <button 
        onClick={onClick} 
        className={`flex-1 flex flex-col items-center justify-center py-4 px-2 relative transition-all duration-300
            ${isActive ? 'text-indigo-600' : 'text-gray-400 hover:text-indigo-400'}
        `}
    >
        <span className="text-xl mb-1">{icon}</span>
        <span className={`text-[10px] font-bold tracking-widest uppercase transition-all ${isActive ? 'opacity-100' : 'opacity-70'}`}>
            {label}
        </span>
        {isActive && (
            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-1/2 h-1 bg-indigo-600 rounded-t-full shadow-[0_-2px_10px_rgba(79,70,229,0.5)]"></div>
        )}
    </button>
);

const ReadOnlyBanner = () => (
    <div className="mb-6 bg-sky-50/80 backdrop-blur-md border border-sky-200 text-sky-800 p-4 rounded-[1.5rem] flex items-start space-x-3 shadow-sm">
        <div className="w-8 h-8 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0 mt-0.5">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-sky-600" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" /></svg>
        </div>
        <div>
            <p className="text-sm font-extrabold tracking-tight">Mode Baca-Saja</p>
            <p className="text-xs mt-1 text-sky-700/80 font-medium leading-relaxed">Anda sedang melihat data yang dibagikan oleh orang tua. Anda dapat menambah hasil pemeriksaan dan intervensi.</p>
        </div>
    </div>
);

const getAssessmentTypes = (assessment) => {
    const types = [];
    if (assessment.perkembangan) types.push('KPSP');
    if (assessment.mental) types.push('KMME');
    if (assessment.pertumbuhan) types.push('Fisik');
    if (assessment.dayaDengar) types.push('TDD'); 
    if (assessment.pemeriksaanMata) types.push('Mata'); 
    if (assessment.chat) types.push('CHAT');
    if (assessment.gpph) types.push('GPPH');
    return types.length > 0 ? types.join(', ') : 'Umum';
};

export default function ChildDetailScreen({ user, child, navigate, showToast }) {
    const [assessments, setAssessments] = useState([]);
    const [immunizations, setImmunizations] = useState({});
    const [activeIntervention, setActiveIntervention] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showShareModal, setShowShareModal] = useState(false);
    const [showChildActionSheet, setShowChildActionSheet] = useState(false);
    const [showHistoryActionSheet, setShowHistoryActionSheet] = useState(null);
    const [activeTab, setActiveTab] = useState('history');

    const fetchData = useCallback(async () => {
        if (!user || !child) return;
        setLoading(true);
        try {
            const ownerUid = child.ownerUid || user.uid;
            const basePath = `users/${ownerUid}/children/${child.id}`;

            const assessmentsPath = `${basePath}/assessments`;
            const qAssessments = query(collection(db, assessmentsPath), orderBy('createdAt', 'desc'));
            const assessmentSnapshot = await getDocs(qAssessments);
            setAssessments(assessmentSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));

            const immunizationsRef = doc(db, `${basePath}/privateData/immunization`);
            const immunizationsSnap = await getDoc(immunizationsRef);
            if (immunizationsSnap.exists()) setImmunizations(immunizationsSnap.data());

            const plansPath = `${basePath}/interventionPlans`;
            const qPlans = query(collection(db, plansPath), orderBy('createdAt', 'desc'));
            const plansSnapshot = await getDocs(qPlans);
            if (!plansSnapshot.empty) {
                const activePlan = plansSnapshot.docs.map(d => d.data()).find(p => p.status === 'active');
                setActiveIntervention(activePlan);
            }

        } catch (error) {
            console.error("Error fetching data:", error);
            setError("Gagal memuat data anak. Pastikan Anda memiliki izin.");
        } finally {
            setLoading(false);
        }
    }, [user, child]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleDeleteChild = async () => {
        try {
            const childDocRef = doc(db, `users/${user.uid}/children/${child.id}`);
            await deleteDoc(childDocRef);
            showToast('Data anak berhasil dihapus.');
            navigate('home');
        } catch (error) {
            console.error("Error deleting child:", error);
            showToast("Gagal menghapus data anak.", "error");
        }
    };

    const handleDeleteAssessment = async (assessmentId) => {
        try {
            const assessmentPath = `users/${user.uid}/children/${child.id}/assessments/${assessmentId}`;
            await deleteDoc(doc(db, assessmentPath));
            fetchData();
            showToast('Riwayat pemeriksaan berhasil dihapus.');
        } catch (error) {
            console.error("Error deleting assessment:", error);
            showToast("Gagal menghapus riwayat.", "error");
        }
    };

    const childActionOptions = [
        { label: 'Edit Data Anak', action: () => navigate('editChild', child) },
        { label: 'Hapus Data Anak', isDestructive: true, action: handleDeleteChild },
    ];

    const historyActionOptions = (assessmentId) => [
        { label: 'Hapus Riwayat Ini', isDestructive: true, action: () => handleDeleteAssessment(assessmentId) },
    ];

    const getStatusColor = (status) => {
        if (!status) return 'bg-gray-100 text-gray-600';
        if (status.includes('Sesuai') || status.includes('Normal') || status.includes('Rendah')) return 'bg-emerald-100 text-emerald-700';
        if (status.includes('Meragukan') || status.includes('Sedang')) return 'bg-amber-100 text-amber-700';
        return 'bg-rose-100 text-rose-700';
    };

    const renderTabContent = () => {
        switch (activeTab) {
            case 'chart':
                return assessments.length > 1 ? (
                    <div className="bg-white/60 backdrop-blur-2xl rounded-[1.5rem] p-5 border border-white shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
                        <GrowthChart assessments={assessments} childGender={child.gender} />
                    </div>
                ) : (
                    <div className="text-center py-10 px-6 bg-white/50 backdrop-blur-xl rounded-[2rem] border border-white/60 shadow-sm">
                        <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center text-3xl mx-auto mb-4 grayscale opacity-50">📈</div>
                        <h3 className="text-lg font-bold text-gray-800 mb-1">Data Belum Cukup</h3>
                        <p className="text-sm text-gray-500 leading-relaxed">Grafik pertumbuhan akan muncul setelah ada minimal 2 data pemeriksaan fisik.</p>
                    </div>
                );
            case 'immunization':
                return (
                    <div className="bg-white/60 backdrop-blur-2xl rounded-[1.5rem] p-5 border border-white shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
                        <ImmunizationScreen user={user} child={child} />
                    </div>
                );
            case 'history':
            default:
                return (
                    <div className="space-y-4">
                        {loading ? (
                            <div className="flex justify-center py-10"><Spinner /></div>
                        ) : assessments.length === 0 ? (
                            <div className="text-center py-10 px-6 bg-white/50 backdrop-blur-xl rounded-[2rem] border border-white/60 shadow-sm">
                                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center text-3xl mx-auto mb-4 grayscale opacity-50">📭</div>
                                <h3 className="text-lg font-bold text-gray-800 mb-1">Belum Ada Riwayat</h3>
                                <p className="text-sm text-gray-500 leading-relaxed">Lakukan pemeriksaan pertama untuk memantau tumbuh kembang anak.</p>
                            </div>
                        ) : (
                            assessments.map(asmnt => (
                                <div key={asmnt.id} className="group bg-white/70 backdrop-blur-xl border border-white rounded-[1.5rem] p-4 shadow-[0_4px_20px_rgba(0,0,0,0.03)] flex items-center justify-between transition-all hover:shadow-[0_8px_30px_rgba(0,0,0,0.08)] hover:-translate-y-0.5">
                                    <div className="flex-1">
                                        <div className="flex items-center space-x-2 mb-1.5">
                                            <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold tracking-widest uppercase ${getStatusColor(asmnt.overallStatus)}`}>
                                                {asmnt.overallStatus || 'Belum Selesai'}
                                            </span>
                                            <span className="text-xs font-bold text-gray-400">
                                                {getAssessmentTypes(asmnt)}
                                            </span>
                                        </div>
                                        <h4 className="font-extrabold text-gray-800 text-[15px]">
                                            {asmnt.createdAt.toDate().toLocaleString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' })}
                                        </h4>
                                        <p className="text-xs text-gray-500 font-medium">
                                            {asmnt.createdAt.toDate().toLocaleString('id-ID', { hour: '2-digit', minute: '2-digit' })} WIB
                                        </p>
                                    </div>
                                    <div className="flex flex-col space-y-2 ml-3">
                                        <button 
                                            onClick={() => navigate('results', child, asmnt)}
                                            className="w-10 h-10 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center text-lg shadow-sm hover:bg-indigo-100 transition-colors"
                                        >
                                            👁️
                                        </button>
                                        {!child.isShared && (
                                            <button 
                                                onClick={() => setShowHistoryActionSheet(asmnt.id)}
                                                className="w-10 h-10 bg-gray-50 text-gray-600 rounded-xl flex items-center justify-center text-lg shadow-sm hover:bg-gray-100 transition-colors"
                                            >
                                                ⚙️
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                );
        }
    };

    return (
        <div className="min-h-screen bg-[#F8FAFC] font-sans selection:bg-indigo-200 pb-28 relative">
            {/* Animated Background Mesh */}
            <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
                <div className="absolute top-[0%] left-[-10%] w-[50vw] h-[50vw] bg-indigo-200 rounded-full mix-blend-multiply filter blur-[100px] opacity-40 animate-blob"></div>
                <div className="absolute top-[30%] right-[-10%] w-[60vw] h-[60vw] bg-sky-200 rounded-full mix-blend-multiply filter blur-[100px] opacity-30 animate-blob animation-delay-2000"></div>
                <div className="absolute bottom-[-10%] left-[20%] w-[70vw] h-[70vw] bg-purple-200 rounded-full mix-blend-multiply filter blur-[100px] opacity-30 animate-blob animation-delay-4000"></div>
                <div className="absolute inset-0 bg-white/40 backdrop-blur-[50px]"></div>
            </div>

            {showShareModal && <ShareChildModal user={user} child={child} closeModal={() => setShowShareModal(false)} />}
            {showChildActionSheet && <ActionSheet title="Opsi Anak" options={childActionOptions} onCancel={() => setShowChildActionSheet(false)} />}
            {showHistoryActionSheet && <ActionSheet title="Opsi Riwayat" options={historyActionOptions(showHistoryActionSheet)} onCancel={() => setShowHistoryActionSheet(null)} />}
            
            <div className="relative z-10">
                {/* Header Profile Section */}
                <div className="pt-8 px-5 pb-6 bg-white/30 backdrop-blur-md border-b border-white shadow-sm rounded-b-[2.5rem] mb-6">
                    {child.isShared && <ReadOnlyBanner />}
                    {error && (
                        <div className="mb-4 bg-rose-50 border border-rose-200 text-rose-600 p-3 rounded-xl text-sm font-medium text-center">
                            {error}
                        </div>
                    )}
                    
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <div className="w-16 h-16 bg-gradient-to-tr from-indigo-500 to-purple-500 rounded-[1.25rem] flex items-center justify-center text-white text-2xl font-extrabold shadow-lg shadow-indigo-200 border-2 border-white">
                                {child.name.charAt(0).toUpperCase()}
                            </div>
                            <div>
                                <h2 className="text-2xl font-extrabold text-gray-800 tracking-tight leading-tight">{child.name}</h2>
                                <p className="text-xs font-bold text-indigo-500 mt-1 uppercase tracking-widest">{calculateAge(child.dob.toDate())}</p>
                                <p className="text-xs text-gray-500 mt-0.5 capitalize">{child.gender}</p>
                            </div>
                        </div>
                        {!child.isShared && (
                            <div className="flex flex-col space-y-2">
                                <button onClick={() => setShowChildActionSheet(true)} className="w-10 h-10 bg-white/80 rounded-xl flex items-center justify-center text-xl shadow-sm border border-gray-100 hover:bg-gray-50 transition-colors">
                                    ⚙️
                                </button>
                                <button onClick={() => setShowShareModal(true)} className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center text-xl shadow-sm border border-indigo-100 hover:bg-indigo-100 transition-colors">
                                    🔗
                                </button>
                            </div>
                        )}
                    </div>
                    
                    {!child.isShared && (
                        <Button 
                            onClick={() => navigate('assessmentHub', child)} 
                            className="w-full bg-gray-900 text-white hover:bg-gray-800 shadow-xl py-4 rounded-xl mt-6 transition-all font-bold"
                        >
                            <span className="mr-2">⚡</span> Mulai Pemeriksaan Baru
                        </Button>
                    )}
                </div>

                <div className="px-5 space-y-6">
                    {activeIntervention && (
                        <div className="bg-white/60 backdrop-blur-2xl rounded-[1.5rem] p-1 border border-white shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
                            <InterventionPlanCard user={user} child={child} plan={activeIntervention} />
                        </div>
                    )}

                    <div className="bg-gradient-to-r from-sky-400 to-indigo-500 rounded-[1.5rem] p-[2px] shadow-lg shadow-indigo-200">
                        <button 
                            onClick={() => navigate('reportCard', child, { assessments, immunizations })} 
                            className="w-full bg-white rounded-[1.4rem] py-4 px-4 flex items-center justify-between transition-all hover:bg-gray-50"
                        >
                            <div className="flex items-center space-x-3">
                                <span className="text-2xl">🏆</span>
                                <span className="font-extrabold text-gray-800">Rapor Tumbuh Kembang</span>
                            </div>
                            <span className="text-gray-400">➔</span>
                        </button>
                    </div>

                    {/* Tabs Navigation */}
                    <div className="bg-white/50 backdrop-blur-xl p-1.5 rounded-[1.5rem] border border-white shadow-sm flex items-center">
                        <TabButton label="Riwayat" icon="📋" isActive={activeTab === 'history'} onClick={() => setActiveTab('history')} />
                        <TabButton label="Grafik" icon="📈" isActive={activeTab === 'chart'} onClick={() => setActiveTab('chart')} />
                        <TabButton label="Imunisasi" icon="💉" isActive={activeTab === 'immunization'} onClick={() => setActiveTab('immunization')} />
                    </div>
                    
                    {/* Tab Content */}
                    <div className="mt-6">
                        {renderTabContent()}
                    </div>
                </div>
            </div>

            <style>{`
                @keyframes blob {
                    0% { transform: translate(0px, 0px) scale(1); }
                    33% { transform: translate(30px, -50px) scale(1.1); }
                    66% { transform: translate(-20px, 20px) scale(0.9); }
                    100% { transform: translate(0px, 0px) scale(1); }
                }
                .animate-blob { animation: blob 15s infinite alternate ease-in-out; }
                .animation-delay-2000 { animation-delay: 2s; }
                .animation-delay-4000 { animation-delay: 4s; }
            `}</style>
        </div>
    );
}
